import { useContext, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { MediaFolderTree } from '@atom/types/media';
import { addToSet, toggleFromSet } from '@atom/utilities/setUtilities';

import { ROOT_FOLDER_ID } from '../constants';
import FilesTabContext from '../FilesTabContext';
import { treeWithPendingChild } from '../filesTabUtilities';
import { PendingEdit } from '../types';

import MoveFolderTreeContext from './MoveFolderTreeContext';
import MoveFolderTreeView from './MoveFolderTreeView';

type MoveFolderTreeProps = {
  open: boolean;
  isFolderMove: boolean;
  movingFolderId: string;
  disabledIds: Set<string>;
  selectedId: string;
  onSelectionChange: (folder: MediaFolderTree | null) => void;
  onEditingChange: (isEditing: boolean) => void;
};

const MoveFolderTree = ({
  open,
  isFolderMove,
  movingFolderId,
  disabledIds,
  selectedId,
  onSelectionChange,
  onEditingChange,
}: MoveFolderTreeProps) => {
  const { folderTree, createFolder, editFolder } = useContext(FilesTabContext);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    new Set([ROOT_FOLDER_ID]),
  );
  const [editing, setEditing] = useState<PendingEdit | null>(null);
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setExpandedIds(new Set([ROOT_FOLDER_ID]));
      setEditing(null);
      setLoadingUpdate(false);
    }
  }, [open]);

  useEffect(() => {
    setEditing(null);
    setLoadingUpdate(false);
  }, [folderTree]);

  useEffect(() => {
    onEditingChange(editing !== null);
  }, [editing, onEditingChange]);

  if (!folderTree) {
    return null;
  }

  const derivedTree =
    editing?.kind === 'create'
      ? treeWithPendingChild(folderTree, editing.parentFolderId, {
          id: editing.tempId,
          name: editing.name,
        })
      : folderTree;

  const handleToggleExpand = (id: string) => {
    setExpandedIds(toggleFromSet(expandedIds, id));
  };

  const handleStartCreate = (parentId: string) => {
    setExpandedIds(prev => addToSet(prev, parentId));
    setEditing({
      kind: 'create',
      parentFolderId: parentId,
      tempId: uuid(),
      name: 'Untitled',
    });
  };

  const handleChangeEditingName = (name: string) => {
    if (!editing) {
      return;
    }

    setEditing({ ...editing, name });
  };

  const handleSaveEditing = () => {
    if (!editing) {
      return;
    }

    setLoadingUpdate(true);
    if (editing.kind === 'create') {
      createFolder(editing.parentFolderId, editing.name);
    } else {
      editFolder(editing.id, editing.name);
    }
  };

  const contextValue = {
    expandedIds,
    onToggleExpand: handleToggleExpand,
    selectedId,
    onSelect: onSelectionChange,
    disabledIds,
    movingFolderId,
    isFolderMove,
    editing,
    loadingUpdate,
    onStartCreate: handleStartCreate,
    onStartRename: (id: string, name: string) =>
      setEditing({ kind: 'rename', id, name }),
    onChangeEditingName: handleChangeEditingName,
    onSaveEditing: handleSaveEditing,
    onCancelEditing: () => setEditing(null),
  };

  return (
    <MoveFolderTreeContext.Provider value={contextValue}>
      <MoveFolderTreeView tree={derivedTree} />
    </MoveFolderTreeContext.Provider>
  );
};

export default MoveFolderTree;
